// sim/discovery.ts — the EVOLUTION DISCOVERY observer: an additive, read-only
// layer that watches each colony's gene pool and AUTO-DISCOVERS emergent "morph"
// species the moment a colony satisfies a hidden synergy (a set of required gene
// types + traits, authored in src/sim/discoveryData.ts).
//
// WHAT THIS IS (and is NOT):
//   This is an OBSERVER, exactly like the leaderboard / evolution-box. It reads
//   the public accessors of the GeneInventory (firstOf / nextOf / typeOf / traitOf
//   / countOf) and writes ONLY its own per-morph + per-colony columns. It never
//   mutates genes/colonies and never touches the RNG — discovery is a PURE
//   FUNCTION of gene-pool state, so same seed + ticks ⇒ identical archives.
//
// THREE ARCHIVES it tracks:
//   1. Species Archive   — every morph's status / discoverer / express counts.
//   2. Extinct Branch    — morphs whose last expresser was lost (revivable).
//   3. Discovery Archive — a fixed-size RING of recent discovery EVENTS (the
//                          headless "animation" seam: it emits DATA, not pixels).
//
// PERMANENCE: a discovered morph is forever KNOWN (Undiscovered → Active). If a
//   colony later stops expressing it, the morph goes Extinct (its discoverer and
//   discovery tick are preserved); regaining an expresser Revives it.
//
// CONCEALMENT: the per-morph recipe masks are PRIVATE. No accessor reveals an
//   undiscovered morph's recipe — "players should not know every possible
//   combination" until the Brood evolves into it.
//
// DATA-ORIENTED + NO HOT-PATH ALLOCATION (DESIGN.md §2 / EFFICIENCY.md §B):
//   Per-morph and per-colony state are flat Int32Array columns. The recipe masks
//   are precomputed ONCE in the constructor. The per-tick scan reuses two fixed
//   scratch arrays and a fixed event ring — `update` allocates nothing.
//
// DETERMINISM: pure integer state (exact FNV hashing). No RNG. Fixed iteration
//   order (ascending colony id, ascending morph id). Same seed + ticks ⇒
//   identical state; a bulk N-tick step == N single steps.

import {
  NIL,
  GENE_TRAIT_COUNT,
  DISCOVERY_STATUS,
  DISCOVERY_EVENT,
} from '../types.ts';
import type { System, Tick, ColonyId, DiscoveryConfig, DiscoveryRenownSource } from '../types.ts';
import type { GeneInventory } from './genes.ts';
import {
  MORPH_COUNT,
  MORPH_REQ_TYPES,
  MORPH_REQ_TRAITS,
  MORPH_TIER,
} from './discoveryData.ts';

/**
 * Pure subset check shared by the system and unit tests: a morph is expressed
 * when the colony's `typeMask` covers ALL of the morph's required-type bits and
 * its `traitMask` covers ALL required-trait bits. Allocation-free, integer-only.
 */
export function morphExpressed(
  typeMask: number,
  traitMask: number,
  reqTypeMask: number,
  reqTraitMask: number,
): boolean {
  return (typeMask & reqTypeMask) === reqTypeMask && (traitMask & reqTraitMask) === reqTraitMask;
}

/**
 * The Evolution-Discovery observer. Runs on a cadence (BEFORE the leaderboard so
 * renown is fresh the same tick), scanning every colony's gene pool, resolving
 * which morphs are currently expressed, and flipping morph lifecycle state +
 * emitting events accordingly.
 */
export class DiscoverySystem implements System, DiscoveryRenownSource {
  readonly name = 'discovery';

  private readonly genes: GeneInventory;
  private readonly source: { colonyCount(): number };
  private readonly config: DiscoveryConfig;
  readonly maxColonies: number;
  private readonly clock: { tick: number };

  // --- Precomputed recipe masks. CONCEALMENT: these are ECMAScript hard-private
  //     (#) fields, not just TS `private` — so a morph's recipe is genuinely
  //     unreachable at runtime (not merely type-checked away). "Players should not
  //     know every possible combination." Built once in the constructor. ---
  /** Σ (1<<type) over MORPH_REQ_TYPES[m]. */
  readonly #reqTypeMask: Int32Array;
  /** Σ (1<<trait) over MORPH_REQ_TRAITS[m]. */
  readonly #reqTraitMask: Int32Array;
  /** Cached MORPH_TIER[m] (RARITY.* tier). Tier is public; the recipe is not. */
  private readonly tier: Int32Array;

  // --- Per-morph columns (SoA, index by morph id). ---
  /** DISCOVERY_STATUS.* — starts Undiscovered. */
  private readonly status: Int32Array;
  /** Colony id of the first discoverer, or NIL. */
  private readonly discoverer: Int32Array;
  /** Tick the morph was first discovered (stable once set). */
  private readonly discoveryTick: Int32Array;
  /** Colonies expressing the morph in the most recent scan. */
  private readonly expressersNow: Int32Array;
  /** Highest expressersNow ever seen. */
  private readonly peakExpressers: Int32Array;
  /** Times the morph went extinct. */
  private readonly extinctions: Int32Array;
  /** Times the morph was revived from extinction. */
  private readonly revivals: Int32Array;
  /** Tick of the morph's most recent extinction (for the Extinct archive). */
  private readonly lastEventTick: Int32Array;

  // --- Per-colony columns (SoA, index by colony id). ---
  /** Sticky discoverer renown — only ever grows. */
  private readonly discoveryCredit: Int32Array;
  /** Volatile renown from currently-expressed morphs — recomputed each scan. */
  private readonly expressRenown: Int32Array;
  /** Lifetime count of morphs this colony was first to discover. */
  private readonly discoveriesMade: Int32Array;
  /** Morphs this colony currently expresses (most recent scan). */
  private readonly speciesExpressedNow: Int32Array;

  // --- Discovery-event RING (the ANIMATION SEAM — emits DATA, not pixels). ---
  private readonly evSpecies: Int32Array;
  private readonly evColony: Int32Array;
  private readonly evTick: Int32Array;
  private readonly evKind: Int32Array;
  private readonly evAnimStyle: Int32Array;
  private readonly evAnimDuration: Int32Array;
  /** Next write slot in the ring (wraps). */
  private evHead = 0;
  /** Live events currently in the ring (≤ capacity). */
  private evCount = 0;
  /** Monotonic count of events ever emitted (never wraps). */
  private evTotal = 0;
  private readonly ringCap: number;

  // --- Reused scratch (per-update; never allocates in the scan). ---
  /** Per-morph expresser tally for the current scan. */
  private readonly morphExpressers: Int32Array;
  /** First (lowest-cid) colony to express each morph this scan, or NIL. */
  private readonly morphFirstExpresser: Int32Array;

  // --- Global counters (deterministic; folded into the hash, read by tests). ---
  discoveriesTotal = 0;
  extinctionsTotal = 0;
  revivalsTotal = 0;
  scansTotal = 0;
  activeSpeciesNow = 0;

  constructor(
    genes: GeneInventory,
    source: { colonyCount(): number },
    config: DiscoveryConfig,
    maxColonies: number,
    clock: { tick: number },
  ) {
    if (config.cadence <= 0) throw new Error('DiscoverySystem cadence must be > 0');
    if (config.eventRingCapacity <= 0)
      throw new Error('DiscoverySystem eventRingCapacity must be > 0');
    if (maxColonies <= 0) throw new Error('DiscoverySystem maxColonies must be > 0');

    this.genes = genes;
    this.source = source;
    this.config = config;
    this.maxColonies = maxColonies;
    this.clock = clock;

    // --- Precompute the per-morph recipe masks ONCE (concealed thereafter). ---
    this.#reqTypeMask = new Int32Array(MORPH_COUNT);
    this.#reqTraitMask = new Int32Array(MORPH_COUNT);
    this.tier = new Int32Array(MORPH_COUNT);
    for (let m = 0; m < MORPH_COUNT; m++) {
      let tMask = 0;
      const types = MORPH_REQ_TYPES[m] as readonly number[];
      for (let i = 0; i < types.length; i++) tMask |= 1 << (types[i] as number);
      this.#reqTypeMask[m] = tMask;

      let trMask = 0;
      const traits = MORPH_REQ_TRAITS[m] as readonly number[];
      for (let i = 0; i < traits.length; i++) trMask |= 1 << (traits[i] as number);
      this.#reqTraitMask[m] = trMask;

      this.tier[m] = MORPH_TIER[m] as number;
    }

    // --- Per-morph columns. ---
    this.status = new Int32Array(MORPH_COUNT); // DISCOVERY_STATUS.Undiscovered == 0
    this.discoverer = new Int32Array(MORPH_COUNT).fill(NIL);
    this.discoveryTick = new Int32Array(MORPH_COUNT);
    this.expressersNow = new Int32Array(MORPH_COUNT);
    this.peakExpressers = new Int32Array(MORPH_COUNT);
    this.extinctions = new Int32Array(MORPH_COUNT);
    this.revivals = new Int32Array(MORPH_COUNT);
    this.lastEventTick = new Int32Array(MORPH_COUNT);

    // --- Per-colony columns. ---
    this.discoveryCredit = new Int32Array(maxColonies);
    this.expressRenown = new Int32Array(maxColonies);
    this.discoveriesMade = new Int32Array(maxColonies);
    this.speciesExpressedNow = new Int32Array(maxColonies);

    // --- Event ring. ---
    this.ringCap = config.eventRingCapacity;
    this.evSpecies = new Int32Array(this.ringCap);
    this.evColony = new Int32Array(this.ringCap);
    this.evTick = new Int32Array(this.ringCap);
    this.evKind = new Int32Array(this.ringCap);
    this.evAnimStyle = new Int32Array(this.ringCap);
    this.evAnimDuration = new Int32Array(this.ringCap);

    // --- Scratch. ---
    this.morphExpressers = new Int32Array(MORPH_COUNT);
    this.morphFirstExpresser = new Int32Array(MORPH_COUNT);
  }

  // --- Event ring (the headless animation seam) ------------------------------

  /**
   * Emit one discovery event into the ring (overwriting the oldest when full).
   * The animation is DATA: `animStyle` = the morph's tier, `animDuration` =
   * animBaseDuration + tier*animDurationPerTier. Allocation-free.
   */
  private emit(species: number, colony: number, tick: number, kind: number): void {
    const t = this.tier[species] as number;
    const slot = this.evHead;
    this.evSpecies[slot] = species;
    this.evColony[slot] = colony;
    this.evTick[slot] = tick;
    this.evKind[slot] = kind;
    this.evAnimStyle[slot] = t;
    this.evAnimDuration[slot] = this.config.animBaseDuration + t * this.config.animDurationPerTier;

    this.evHead = (this.evHead + 1) % this.ringCap;
    if (this.evCount < this.ringCap) this.evCount++;
    this.evTotal++;
  }

  // --- Per-tick driver -------------------------------------------------------

  /**
   * One discovery scan (on the cadence). For each colony in ascending id order
   * (capped at maxColonies), build its type/trait masks, then — if it holds at
   * least minPoolToExpress genes — tally which morphs it expresses. A post-pass
   * over morphs in ascending id order flips lifecycle state (Discover / Revive /
   * Extinct), emits events, and recomputes the live active count. Deterministic,
   * allocation-free.
   */
  update(tick: Tick): void {
    if (tick % this.config.cadence !== 0) return;
    this.scansTotal++;

    const genes = this.genes;
    const reqType = this.#reqTypeMask;
    const reqTrait = this.#reqTraitMask;
    const tierOf = this.tier;
    const expressByTier = this.config.expressRenownByTier;
    const minPool = this.config.minPoolToExpress;

    const morphExpressers = this.morphExpressers;
    const morphFirst = this.morphFirstExpresser;
    morphExpressers.fill(0);
    morphFirst.fill(NIL);

    // --- Colony scan (ascending id order). ---
    const n = Math.min(this.source.colonyCount(), this.maxColonies);
    for (let cid = 0; cid < n; cid++) {
      this.expressRenown[cid] = 0;
      this.speciesExpressedNow[cid] = 0;

      // Build the colony's presence masks from a single gene walk.
      let typeMask = 0;
      let traitMask = 0;
      for (let gid = genes.firstOf(cid); gid !== NIL; gid = genes.nextOf(gid)) {
        typeMask |= 1 << genes.typeOf(gid);
        for (let t = 0; t < GENE_TRAIT_COUNT; t++) {
          if (genes.traitOf(gid, t) > 0) traitMask |= 1 << t;
        }
      }

      // A pool too small to express anything contributes nothing.
      if (genes.countOf(cid) < minPool) continue;

      let renown = 0;
      let expressed = 0;
      for (let m = 0; m < MORPH_COUNT; m++) {
        const rt = reqType[m] as number;
        const rtr = reqTrait[m] as number;
        if ((typeMask & rt) === rt && (traitMask & rtr) === rtr) {
          morphExpressers[m] = (morphExpressers[m] as number) + 1;
          if ((morphFirst[m] as number) === NIL) morphFirst[m] = cid;
          renown += expressByTier[tierOf[m] as number] as number;
          expressed++;
        }
      }
      this.expressRenown[cid] = renown;
      this.speciesExpressedNow[cid] = expressed;
    }

    // --- Morph lifecycle post-pass (ascending morph id order). ---
    const discByTier = this.config.discoveryRenownByTier;
    let active = 0;
    for (let m = 0; m < MORPH_COUNT; m++) {
      const count = morphExpressers[m] as number;
      const prev = this.status[m] as number;
      const t = tierOf[m] as number;

      if (count > 0 && prev === DISCOVERY_STATUS.Undiscovered) {
        // DISCOVER — first ever. Credit the (lowest-cid) first expresser.
        const who = morphFirst[m] as number;
        this.status[m] = DISCOVERY_STATUS.Active;
        this.discoverer[m] = who;
        this.discoveryTick[m] = tick;
        this.discoveriesTotal++;
        this.discoveryCredit[who] = (this.discoveryCredit[who] as number) + (discByTier[t] as number);
        this.discoveriesMade[who] = (this.discoveriesMade[who] as number) + 1;
        this.emit(m, who, tick, DISCOVERY_EVENT.Discovered);
      } else if (count > 0 && prev === DISCOVERY_STATUS.Extinct) {
        // REVIVE — an extinct branch regained an expresser. Discoverer is kept.
        this.status[m] = DISCOVERY_STATUS.Active;
        this.revivals[m] = (this.revivals[m] as number) + 1;
        this.revivalsTotal++;
        this.emit(m, morphFirst[m] as number, tick, DISCOVERY_EVENT.Revived);
      } else if (count === 0 && prev === DISCOVERY_STATUS.Active) {
        // EXTINCT — lost the last expresser. Discoverer + discoveryTick preserved.
        this.status[m] = DISCOVERY_STATUS.Extinct;
        this.extinctions[m] = (this.extinctions[m] as number) + 1;
        this.extinctionsTotal++;
        this.lastEventTick[m] = tick;
        this.emit(m, NIL, tick, DISCOVERY_EVENT.Extinct);
      }

      this.expressersNow[m] = count;
      if (count > (this.peakExpressers[m] as number)) this.peakExpressers[m] = count;
      if ((this.status[m] as number) === DISCOVERY_STATUS.Active) active++;
    }
    this.activeSpeciesNow = active;
  }

  // --- DiscoveryRenownSource (the leaderboard seam) --------------------------

  /** Total discovery renown for `cid`: sticky discoverer credit + live express. */
  renownAt(cid: ColonyId): number {
    return (this.discoveryCredit[cid] as number) + (this.expressRenown[cid] as number);
  }

  // --- Per-morph accessors (archives / hash / tests) ------------------------
  // NOTE: deliberately NO accessor exposes a morph's recipe masks (concealment).

  statusOf(m: number): number {
    return this.status[m] as number;
  }
  discovererOf(m: number): number {
    return this.discoverer[m] as number;
  }
  discoveryTickOf(m: number): number {
    return this.discoveryTick[m] as number;
  }
  expressersOf(m: number): number {
    return this.expressersNow[m] as number;
  }
  peakExpressersOf(m: number): number {
    return this.peakExpressers[m] as number;
  }
  extinctionsOf(m: number): number {
    return this.extinctions[m] as number;
  }
  revivalsOf(m: number): number {
    return this.revivals[m] as number;
  }
  /** Tick of the morph's most recent extinction event (0 if it never went extinct). */
  lastEventTickOf(m: number): number {
    return this.lastEventTick[m] as number;
  }
  /** True once the morph has EVER been discovered (Active or Extinct). */
  isDiscovered(m: number): boolean {
    return (this.status[m] as number) !== DISCOVERY_STATUS.Undiscovered;
  }

  // --- Catalogue passthrough (no recipe leak). ---
  morphCount(): number {
    return MORPH_COUNT;
  }
  /** The morph's prestige tier (RARITY.*). Tier is public; the recipe is not. */
  tierOf(m: number): number {
    return this.tier[m] as number;
  }

  // --- Per-colony accessors. ---
  discoveryCreditAt(cid: ColonyId): number {
    return this.discoveryCredit[cid] as number;
  }
  expressRenownAt(cid: ColonyId): number {
    return this.expressRenown[cid] as number;
  }
  discoveriesMadeAt(cid: ColonyId): number {
    return this.discoveriesMade[cid] as number;
  }
  speciesExpressedAt(cid: ColonyId): number {
    return this.speciesExpressedNow[cid] as number;
  }

  // --- Archive counts. ---
  /** Morphs ever discovered (status != Undiscovered). */
  discoveredCount(): number {
    let c = 0;
    for (let m = 0; m < MORPH_COUNT; m++) {
      if ((this.status[m] as number) !== DISCOVERY_STATUS.Undiscovered) c++;
    }
    return c;
  }
  /** Morphs currently Active. */
  activeSpeciesCount(): number {
    let c = 0;
    for (let m = 0; m < MORPH_COUNT; m++) {
      if ((this.status[m] as number) === DISCOVERY_STATUS.Active) c++;
    }
    return c;
  }
  /** Morphs currently Extinct (discovered but unexpressed). */
  extinctSpeciesCount(): number {
    let c = 0;
    for (let m = 0; m < MORPH_COUNT; m++) {
      if ((this.status[m] as number) === DISCOVERY_STATUS.Extinct) c++;
    }
    return c;
  }

  // --- Event ring accessors (chronological: oldest→newest among live). -------

  /** Live events currently held in the ring (≤ capacity). */
  eventCount(): number {
    return this.evCount;
  }
  /** Monotonic total of events ever emitted (never wraps). */
  eventTotal(): number {
    return this.evTotal;
  }

  /**
   * Map a chronological index i in [0, eventCount) — oldest at 0 — to its ring
   * slot. The oldest live event sits at (evHead - evCount), modulo capacity.
   */
  private ringSlot(i: number): number {
    const start = (this.evHead - this.evCount + this.ringCap) % this.ringCap;
    return (start + i) % this.ringCap;
  }

  eventSpeciesAt(i: number): number {
    return this.evSpecies[this.ringSlot(i)] as number;
  }
  eventColonyAt(i: number): number {
    return this.evColony[this.ringSlot(i)] as number;
  }
  eventTickAt(i: number): number {
    return this.evTick[this.ringSlot(i)] as number;
  }
  eventKindAt(i: number): number {
    return this.evKind[this.ringSlot(i)] as number;
  }
  eventAnimStyleAt(i: number): number {
    return this.evAnimStyle[this.ringSlot(i)] as number;
  }
  eventAnimDurationAt(i: number): number {
    return this.evAnimDuration[this.ringSlot(i)] as number;
  }
}
